import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useCms } from "../context/CmsContext";
import SectionHead from "../components/SectionHead";
import * as Constants from "../api/constants";
import { normalizeTheatersList } from "../utils/normalizeTheaters";
import { toDateInputValue } from "../utils/eventDate";
import {
  buildGallerySectionsFromApiTheaters,
  normalizeTheaterFolderKey,
  fetchTheatreGalleryByNames,
} from "../utils/theaterGalleryImages";
import {
  fetchPublicTheaterGalleryManifest,
  resolveTheaterImageSrc,
} from "../utils/theaterHeroSlides";
import "../partyBossTheme.css";
import { resolveCmsAssetUrl } from "../utils/cmsAssetUrl";

function mergeManifestIntoSections(sections, manifestGroups) {
  if (!manifestGroups?.length || !sections?.length) return sections;
  const extraByFolder = new Map(
    manifestGroups.map((g) => [
      String(g.folder || "").toUpperCase(),
      g.images || [],
    ])
  );
  return sections.map((s) => {
    const folderKey = normalizeTheaterFolderKey(s.displayName);
    const extra = extraByFolder.get(folderKey);
    if (!extra?.length) return s;
    const merged = [...s.images];
    for (const u of extra) {
      const ru = resolveTheaterImageSrc(u);
      if (ru && typeof ru === "string" && !merged.includes(ru)) merged.push(ru);
    }
    return { ...s, images: merged };
  });
}

const Gallery = () => {
  const { cms } = useCms();
  const h = cms.home;
  const gallery = h.gallery || [];
  const intro = h.galleryIntro || {};

  const [theatersFromApi, setTheatersFromApi] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [slotsError, setSlotsError] = useState(null);
  const [manifestGroups, setManifestGroups] = useState([]);
  const [dynamicGalleryByKey, setDynamicGalleryByKey] = useState({});

  useEffect(() => {
    const controller = new AbortController();
    const payload = { date: toDateInputValue(null) };

    setSlotsLoading(true);
    setSlotsError(null);

    axios
      .post(Constants.GET_THEATERS_LIST, payload, {
        signal: controller.signal,
      })
      .then((res) => {
        setTheatersFromApi(normalizeTheatersList(res?.data));
      })
      .catch((err) => {
        if (
          controller.signal.aborted ||
          err?.code === "ERR_CANCELED" ||
          err?.name === "CanceledError"
        ) {
          return;
        }
        setSlotsError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load theatres."
        );
        setTheatersFromApi([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setSlotsLoading(false);
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const names = (theatersFromApi || [])
      .map(
        (t) => t?.name ?? t?.theatreName ?? t?.theaterName ?? t?.theatre ?? ""
      )
      .map((s) => String(s).trim())
      .filter(Boolean);
    if (!names.length) {
      setDynamicGalleryByKey({});
      return undefined;
    }
    let cancelled = false;
    fetchTheatreGalleryByNames(names).then((data) => {
      if (!cancelled) setDynamicGalleryByKey(data && typeof data === "object" ? data : {});
    });
    return () => {
      cancelled = true;
    };
  }, [theatersFromApi]);

  useEffect(() => {
    let cancelled = false;
    fetchPublicTheaterGalleryManifest().then((groups) => {
      if (!cancelled) setManifestGroups(groups);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const sectionsBase = useMemo(
    () =>
      buildGallerySectionsFromApiTheaters(
        theatersFromApi,
        cms.theaterMedia,
        dynamicGalleryByKey
      ),
    [theatersFromApi, cms.theaterMedia, dynamicGalleryByKey]
  );

  const theaterSections = useMemo(
    () => mergeManifestIntoSections(sectionsBase, manifestGroups),
    [sectionsBase, manifestGroups]
  );

  useEffect(() => {
    document.body.classList.add("party-boss-home");
    const revealOnScroll = () => {
      document.querySelectorAll(".party-boss-reveal").forEach((item) => {
        const top = item.getBoundingClientRect().top;
        if (top < window.innerHeight - 90) item.classList.add("active");
      });
    };
    revealOnScroll();
    window.addEventListener("scroll", revealOnScroll, { passive: true });
    return () => {
      document.body.classList.remove("party-boss-home");
      window.removeEventListener("scroll", revealOnScroll);
    };
  }, []);

  return (
    <div className="gallery-page party-boss-page w-full overflow-x-clip bg-[#05020a] text-white selection:bg-pink-500/40">
      <div className="party-boss-bg relative z-10 pb-24">
        <SectionHead
          id={intro.id || "gallery"}
          className="scroll-mt-28"
          eyebrow={intro.eyebrow}
          title={intro.title}
          text={intro.text}
        />

        {slotsLoading ? (
          <p className="px-[5%] text-center text-[#c9c1d9] lg:px-[8%]">
            Loading theatres…
          </p>
        ) : null}

        {!slotsLoading && slotsError ? (
          <p className="mx-auto max-w-xl px-[5%] text-center text-red-300/90 lg:px-[8%]">
            {slotsError}
          </p>
        ) : null}

        {!slotsLoading && !slotsError && theaterSections.length > 0 ? (
          <div className="mx-auto flex max-w-6xl flex-col gap-14 px-[5%] lg:gap-16 lg:px-[8%]">
            {theaterSections.map((section) => (
              <article
                key={String(section.id)}
                id={`gallery-theatre-${encodeURIComponent(String(section.id))}`}
                className="scroll-mt-24"
              >
                <header className="mb-6 border-b border-white/10 pb-4">
                  <h2 className="text-2xl font-black tracking-tight text-white md:text-3xl">
                    {section.displayName}
                  </h2>
                </header>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
                  {section.images.map((src, idx) => (
                    <div
                      key={`${section.id}-${idx}`}
                      className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] shadow-lg"
                    >
                      <img
                        src={src}
                        alt=""
                        className="aspect-[4/3] h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        ) : null}

        {!slotsLoading && !slotsError && theaterSections.length === 0 ? (
          <div className="mx-auto max-w-2xl px-[5%] text-center text-[#c9c1d9] lg:px-[8%]">
            <p className="mb-4">
              No gallery photos match the theatres returned for today&apos;s date.
              Use folder names under{" "}
              <code className="rounded bg-white/10 px-1.5 py-0.5 text-xs">
                src/images/Theatre_images/
              </code>{" "}
              that match each theatre name from the API (same names as on the
              booking page), or add images in Admin → Site content → theatre
              media.
            </p>
            {gallery.length === 0 ? (
              <p className="text-sm text-white/40">
                No extra CMS gallery images are configured either.
              </p>
            ) : null}
          </div>
        ) : null}

        {gallery.length > 0 ? (
          <>
            {theaterSections.length > 0 ? (
              <h3 className="party-boss-reveal mb-6 mt-14 px-[5%] text-center text-lg font-black text-white/90 lg:px-[8%]">
                More celebration moments
              </h3>
            ) : null}
            <section className="grid gap-6 px-[5%] pb-8 md:grid-cols-2 lg:grid-cols-3 lg:px-[8%]">
              {gallery.map((item) => {
                const title = item.title;
                const image = item.imageUrl || item.image;
                const big = Boolean(item.big);
                return (
                  <div
                    key={title}
                    className={`party-boss-gallery-item party-boss-reveal relative overflow-hidden rounded-[34px] border border-white/15 ${
                      big ? "min-h-[480px] lg:row-span-2" : "min-h-[240px]"
                    }`}
                  >
                    <img
                      className="h-full w-full object-cover transition duration-700 hover:scale-110"
                      src={resolveCmsAssetUrl(image)}
                      alt=""
                    />
                  </div>
                );
              })}
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Gallery;
